import { providerLabel } from "@/lib/account";
import type { ParamSpec, ProviderInfoWire } from "@/api/types";
import { isRedacted } from "@/lib/utils";

/** Account/exchange providers users can connect as marketplaces. */
export const ENABLED_ACCOUNT_PROVIDERS = ["binance", "mexc", "revolutx"] as const;

export type EnabledAccountProvider = (typeof ENABLED_ACCOUNT_PROVIDERS)[number];

export type MarketplacePickerCard = {
  id: string;
  provider: string;
  title: string;
  subtitle: string;
  /** Prefill account config (e.g. network=DEMO) when a trade type is chosen. */
  staticOptions: Record<string, string>;
};

const BINANCE_ENV_CARDS: Omit<MarketplacePickerCard, "provider">[] = [
  {
    id: "binance:MAINNET",
    title: "Binance",
    subtitle: "Production — live trading on mainnet.",
    staticOptions: { network: "MAINNET" },
  },
  {
    id: "binance:DEMO",
    title: "Binance Demo",
    subtitle: "Demo trading environment — no real funds.",
    staticOptions: { network: "DEMO" },
  },
];

export function normalizeAccountProvider(provider?: string | null): string {
  return String(provider || "").trim().toLowerCase();
}

export function isAccountProviderEnabled(
  provider?: string | null
): provider is EnabledAccountProvider {
  const p = normalizeAccountProvider(provider);
  if (!p) return false;
  return (ENABLED_ACCOUNT_PROVIDERS as readonly string[]).includes(p);
}

/** Enabled providers first (stable ENABLED order), then the rest alphabetically. */
export function sortAccountProviders(providers: string[]): string[] {
  const enabledRank = new Map<string, number>(
    ENABLED_ACCOUNT_PROVIDERS.map((id, i) => [id, i])
  );
  return [...providers].sort((a, b) => {
    const aKey = normalizeAccountProvider(a);
    const bKey = normalizeAccountProvider(b);
    const aEn = enabledRank.has(aKey);
    const bEn = enabledRank.has(bKey);
    if (aEn !== bEn) return aEn ? -1 : 1;
    if (aEn && bEn) return (enabledRank.get(aKey) ?? 0) - (enabledRank.get(bKey) ?? 0);
    return aKey.localeCompare(bKey);
  });
}

/**
 * Expand providers into marketplace picker cards.
 * Binance shows Production + Demo; others are a single card each.
 */
export function buildMarketplacePickerCards(providers: string[]): MarketplacePickerCard[] {
  const cards: MarketplacePickerCard[] = [];
  for (const provider of sortAccountProviders(providers)) {
    if (normalizeAccountProvider(provider) === "binance") {
      for (const env of BINANCE_ENV_CARDS) {
        cards.push({ ...env, provider: "binance" });
      }
      continue;
    }
    cards.push({
      id: provider,
      provider,
      title: providerLabel(provider),
      subtitle: isAccountProviderEnabled(provider)
        ? "Connect API credentials and start mirroring strategies."
        : "This exchange isn’t available in TradeStorm yet.",
      staticOptions: {},
    });
  }
  return cards;
}

/**
 * Spot/futures buttons for a marketplace card, from `configurable.providers`
 * templates (filtered by the card's static options, e.g. network=DEMO).
 */
export function accountProviderTradeTypes(
  info?: ProviderInfoWire | null,
  staticOptions: Record<string, string> = {}
): { spot: boolean; futures: boolean } {
  const templates = info?.templates ?? [];
  const matching = templates.filter((t) =>
    Object.entries(staticOptions).every(
      ([k, v]) => !v || String(t.static_options?.[k] ?? "") === v
    )
  );
  const pool = matching.length > 0 ? matching : templates;

  if (pool.length > 0) {
    const spot = pool.some(
      (t) =>
        t.tags?.includes("spot") ||
        String(t.static_options?.market_type ?? "").toUpperCase() === "SPOT"
    );
    const futures = pool.some(
      (t) =>
        t.tags?.includes("futures") ||
        String(t.static_options?.market_type ?? "").toUpperCase() === "FUTURES"
    );
    if (spot || futures) return { spot, futures };
  }

  const tags = info?.tags ?? [];
  const spot = tags.includes("spot");
  const futures = tags.includes("futures");
  if (spot || futures) return { spot, futures };

  // No metadata — allow both.
  return { spot: true, futures: true };
}

export function accountProviderLabel(provider: string): string {
  return providerLabel(provider);
}

function filled(value: unknown): boolean {
  if (isRedacted(value)) return true;
  return value !== undefined && value !== null && String(value).trim() !== "";
}

/** Why the credentials can't be saved yet, or null when they're complete enough to try. */
export function missingCredentialMessage(specs: ParamSpec[], config: Record<string, unknown>): string | null {
  if (specs.some((s) => s.type === "ed25519") && !filled(config.private_key)) {
    return "Generate an Ed25519 key first";
  }
  const ed25519 = specs.some((s) => s.type === "ed25519");
  if (specs.some((s) => s.name === "api_key") && !filled(config.api_key)) {
    return ed25519
      ? "Paste the API Key Binance shows after you add the public key. The generated key is not enough — Binance still identifies the account with that API Key."
      : "Paste the API key.";
  }
  if (!ed25519) {
    const secret = specs.find((s) => ["secret", "api_secret", "secret_key"].includes(s.name));
    if (secret && !filled(config[secret.name])) return "Paste the secret key.";
  }
  return null;
}

/** Where to create API keys on each exchange. */
export function apiKeyPageUrl(provider: string, network?: string): string | null {
  const p = normalizeAccountProvider(provider);
  if (p === "binance") {
    return String(network || "").toUpperCase() === "DEMO"
      ? "https://demo.binance.com/en/my/settings/api-management"
      : "https://www.binance.com/en/my/settings/api-management";
  }
  if (p === "mexc") return "https://www.mexc.com/user/openapi";
  if (p === "revolutx") return "https://exchange.revolut.com/";
  return null;
}
